import { useEffect, useMemo, useState } from "react";
import api, { apiErr, fmtMoney, fmtDate } from "@/lib/api";
import { PageHeader, Loader, EmptyState } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { StatusBadge } from "@/components/StatusBadge";
import { Plus, BookOpen, MoreVertical, Trash2, Pencil, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";

const emptyLine = () => ({ compte: "", libelle: "", debit: 0, credit: 0 });
const today = () => new Date().toISOString().slice(0, 10);

export default function AccountingEntries() {
  const [items, setItems] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [journals, setJournals] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [fJournal, setFJournal] = useState("all");

  const load = () => api.get("/accounting/entries").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/accounting/accounts").then((r) => setAccounts(r.data)).catch(() => {});
    api.get("/accounting/journals").then((r) => setJournals(r.data)).catch(() => {});
    api.get("/accounting/periods").then((r) => setPeriods(r.data)).catch(() => {});
  }, []);

  const acctOptions = accounts.map((a) => ({ value: a.numero, label: `${a.numero} — ${a.libelle}` }));
  const acctLabel = (n) => (accounts.find((a) => a.numero === n)?.libelle) || "";

  const openNew = () => {
    const activeP = periods.find((p) => p.active) || periods[0];
    setEditing(null);
    setForm({ date: today(), journal_code: journals[0]?.code || "OD", piece: "", libelle: "", period_id: activeP?.id || "", lines: [emptyLine(), emptyLine()] });
    setOpen(true);
  };
  const openEdit = (e) => {
    setEditing(e);
    setForm({ date: e.date, journal_code: e.journal_code, piece: e.piece || "", libelle: e.libelle || "", period_id: e.period_id || "", lines: e.lines.map((l) => ({ compte: l.compte, libelle: l.libelle || "", debit: l.debit || 0, credit: l.credit || 0 })) });
    setOpen(true);
  };

  const setLine = (i, k, v) => setForm((f) => ({ ...f, lines: f.lines.map((l, idx) => idx === i ? { ...l, [k]: k === "debit" || k === "credit" ? Number(v) || 0 : v } : l) }));
  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, emptyLine()] }));
  const rmLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));

  const totals = useMemo(() => {
    if (!form) return { d: 0, c: 0, ok: false };
    const d = form.lines.reduce((s, l) => s + (Number(l.debit) || 0), 0);
    const c = form.lines.reduce((s, l) => s + (Number(l.credit) || 0), 0);
    return { d: Math.round(d * 100) / 100, c: Math.round(c * 100) / 100, ok: Math.abs(d - c) < 0.01 && d > 0 };
  }, [form]);

  const save = async () => {
    if (!totals.ok) { toast.error("L'écriture doit être équilibrée (débit = crédit) et non nulle"); return; }
    const payload = { ...form, lines: form.lines.filter((l) => l.compte && ((l.debit || 0) || (l.credit || 0))).map((l) => ({ ...l, compte_libelle: acctLabel(l.compte) })) };
    setSaving(true);
    try {
      if (editing) await api.put(`/accounting/entries/${editing.id}`, payload);
      else await api.post("/accounting/entries", payload);
      toast.success(editing ? "Écriture mise à jour" : "Écriture enregistrée"); setOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  const validate = async (e) => { try { await api.post(`/accounting/entries/${e.id}/validate`); toast.success("Écriture validée"); load(); } catch (err) { toast.error(apiErr(err)); } };
  const remove = async (e) => { if (!window.confirm("Supprimer cette écriture ?")) return; try { await api.delete(`/accounting/entries/${e.id}`); toast.success("Supprimée"); load(); } catch (err) { toast.error(apiErr(err)); } };

  const filtered = (items || []).filter((e) => fJournal === "all" || e.journal_code === fJournal);

  return (
    <div>
      <PageHeader title="Écritures comptables" subtitle="Saisie en partie double — équilibre débit = crédit">
        <Select value={fJournal} onValueChange={setFJournal}>
          <SelectTrigger className="w-44 mr-2" data-testid="entry-journal-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tous les journaux</SelectItem>{journals.map((j) => <SelectItem key={j.code} value={j.code}>{j.code} — {j.libelle}</SelectItem>)}</SelectContent>
        </Select>
        <Button onClick={openNew} data-testid="add-entry" className="bg-brand-600 hover:bg-brand-700 text-white"><Plus className="h-4 w-4 mr-1.5" />Nouvelle écriture</Button>
      </PageHeader>

      {items === null ? <Loader /> : filtered.length === 0 ? (
        <EmptyState icon={BookOpen} title="Aucune écriture" subtitle="Saisissez votre première écriture comptable." />
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden"><div className="overflow-x-auto scrollbar-thin">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>N°</TableHead><TableHead>Date</TableHead><TableHead>Journal</TableHead><TableHead>Libellé</TableHead><TableHead>Pièce</TableHead><TableHead className="text-right">Débit</TableHead><TableHead className="text-right">Crédit</TableHead><TableHead>Statut</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {filtered.map((e) => (
                <TableRow key={e.id} data-testid={`entry-row-${e.id}`} className="hover:bg-slate-50">
                  <TableCell className="font-mono text-xs text-slate-500">{e.numero}</TableCell>
                  <TableCell className="font-mono text-xs">{fmtDate(e.date)}</TableCell>
                  <TableCell><span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-mono">{e.journal_code}</span></TableCell>
                  <TableCell className="max-w-xs truncate">{e.libelle || "—"}</TableCell>
                  <TableCell className="text-slate-500 text-xs">{e.piece || "—"}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{fmtMoney(e.total_debit)}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{fmtMoney(e.total_credit)}</TableCell>
                  <TableCell><StatusBadge value={e.statut} /></TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`entry-menu-${e.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {e.statut !== "Validée" && <DropdownMenuItem onClick={() => validate(e)} data-testid={`entry-validate-${e.id}`}><CheckCircle2 className="h-4 w-4 mr-2" />Valider</DropdownMenuItem>}
                        {e.statut !== "Validée" && <DropdownMenuItem onClick={() => openEdit(e)}><Pencil className="h-4 w-4 mr-2" />Modifier</DropdownMenuItem>}
                        {e.statut !== "Validée" && <DropdownMenuItem onClick={() => remove(e)} className="text-rose-600"><Trash2 className="h-4 w-4 mr-2" />Supprimer</DropdownMenuItem>}
                        {e.statut === "Validée" && <DropdownMenuItem disabled>Écriture verrouillée</DropdownMenuItem>}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div></div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">{editing ? `Modifier ${editing.numero}` : "Nouvelle écriture"}</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div><label className="text-[11px] text-slate-400 uppercase">Date</label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-9 text-sm" data-testid="entry-date" /></div>
                <div><label className="text-[11px] text-slate-400 uppercase">Journal</label>
                  <Select value={form.journal_code} onValueChange={(v) => setForm({ ...form, journal_code: v })}>
                    <SelectTrigger className="h-9 text-sm" data-testid="entry-journal"><SelectValue /></SelectTrigger>
                    <SelectContent>{journals.map((j) => <SelectItem key={j.code} value={j.code}>{j.code} — {j.libelle}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><label className="text-[11px] text-slate-400 uppercase">Exercice</label>
                  <Select value={form.period_id} onValueChange={(v) => setForm({ ...form, period_id: v })}>
                    <SelectTrigger className="h-9 text-sm" data-testid="entry-period"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{periods.map((p) => <SelectItem key={p.id} value={p.id}>{p.libelle}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><label className="text-[11px] text-slate-400 uppercase">Pièce</label><Input value={form.piece} onChange={(e) => setForm({ ...form, piece: e.target.value })} className="h-9 text-sm" placeholder="N° pièce" data-testid="entry-piece" /></div>
              </div>
              <div><label className="text-[11px] text-slate-400 uppercase">Libellé de l'écriture</label><Input value={form.libelle} onChange={(e) => setForm({ ...form, libelle: e.target.value })} className="h-9 text-sm" placeholder="Ex : Facture client n°…" data-testid="entry-libelle" /></div>

              <div className="rounded-xl border overflow-hidden">
                <div className="grid grid-cols-[1fr_1fr_110px_110px_32px] gap-2 bg-slate-50 px-3 py-2 text-[11px] uppercase font-mono text-slate-400"><span>Compte</span><span>Libellé ligne</span><span className="text-right">Débit</span><span className="text-right">Crédit</span><span></span></div>
                <div className="divide-y">
                  {form.lines.map((l, i) => (
                    <div key={i} className="grid grid-cols-[1fr_1fr_110px_110px_32px] gap-2 items-center px-3 py-1.5" data-testid={`entry-line-${i}`}>
                      <Select value={l.compte} onValueChange={(v) => setLine(i, "compte", v)}>
                        <SelectTrigger className="h-8 text-xs" data-testid={`entry-line-compte-${i}`}><SelectValue placeholder="Compte" /></SelectTrigger>
                        <SelectContent className="max-h-72">{acctOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input value={l.libelle} onChange={(e) => setLine(i, "libelle", e.target.value)} className="h-8 text-xs" placeholder="(optionnel)" />
                      <Input type="number" value={l.debit || ""} onChange={(e) => setLine(i, "debit", e.target.value)} className="h-8 text-xs text-right" data-testid={`entry-line-debit-${i}`} />
                      <Input type="number" value={l.credit || ""} onChange={(e) => setLine(i, "credit", e.target.value)} className="h-8 text-xs text-right" data-testid={`entry-line-credit-${i}`} />
                      <Button type="button" variant="ghost" size="icon" onClick={() => rmLine(i)} className="h-8 w-8 text-slate-400 hover:text-rose-600"><X className="h-4 w-4" /></Button>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-t">
                  <Button type="button" variant="outline" size="sm" onClick={addLine} data-testid="entry-add-line"><Plus className="h-4 w-4 mr-1" />Ligne</Button>
                  <div className="flex items-center gap-4 text-sm font-mono">
                    <span>Débit : <b>{fmtMoney(totals.d)}</b></span>
                    <span>Crédit : <b>{fmtMoney(totals.c)}</b></span>
                    <span className={totals.ok ? "text-emerald-600" : "text-rose-600"} data-testid="entry-balance">{totals.ok ? "✓ Équilibrée" : `Écart : ${fmtMoney(totals.d - totals.c)}`}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} disabled={saving || !totals.ok} data-testid="entry-submit" className="bg-brand-600 hover:bg-brand-700 text-white">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
